import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { ChevronLeft, User, Menu, X, Home, BookOpen, LogOut, CreditCard, Settings, Sparkles } from 'lucide-react'
import QuebonLogo from '@/assets/quebon-logo.svg'

export function Header() {
  const { user, logout, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    setIsMenuOpen(false)
    navigate('/login')
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleHome = () => {
    navigate('/')
  }

  // 홈페이지가 아닐 때만 뒤로가기 버튼 표시
  const showBackButton = location.pathname !== '/'

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-xs">
        <div className="flex h-14 items-center justify-between px-4">
          {/* Left: Back Button + Logo */}
          <div className="flex items-center gap-1">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="flex items-center justify-center w-10 h-10 -ml-2 rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                aria-label="뒤로가기"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <button
              onClick={handleHome}
              className="flex items-center px-1 py-1 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <img src={QuebonLogo} alt="깨봉" className="h-8" />
            </button>
          </div>

          {/* Right: Profile + Menu */}
          <div className="flex items-center gap-1">
            {isAuthenticated && user && (
              <Link
                to="/profile"
                className="flex items-center justify-center w-10 h-10 rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                aria-label="내 프로필"
              >
                <User className="w-5 h-5" />
              </Link>
            )}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              aria-label="메뉴 열기"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      {/* Slide Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-xs"
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Slide Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              {isAuthenticated && user ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium">
                    {(user.displayName || user.fullName || 'U').charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.displayName || user.fullName}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </>
              ) : (
                <span className="text-gray-600">로그인이 필요합니다</span>
              )}
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center w-10 h-10 rounded-full text-gray-400 hover:bg-gray-100 transition-colors"
              aria-label="메뉴 닫기"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu Items */}
          <nav className="p-2">
            {isAuthenticated ? (
              <>
                <Link
                  to="/landing"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Sparkles className="w-5 h-5 text-gray-400" />
                  <span>서비스 소개</span>
                </Link>
                <Link
                  to="/"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Home className="w-5 h-5 text-gray-400" />
                  <span>홈</span>
                </Link>
                <Link
                  to="/learn"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <BookOpen className="w-5 h-5 text-gray-400" />
                  <span>학습하기</span>
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <User className="w-5 h-5 text-gray-400" />
                  <span>내 프로필</span>
                </Link>
                <Link
                  to="/subscription/plans"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <CreditCard className="w-5 h-5 text-gray-400" />
                  <span>구독 관리</span>
                </Link>

                <div className="my-2 border-t border-gray-100" />

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>로그아웃</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <User className="w-5 h-5 text-gray-400" />
                  <span>로그인</span>
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-primary-600 hover:bg-primary-50 transition-colors font-medium"
                >
                  <Settings className="w-5 h-5" />
                  <span>회원가입</span>
                </Link>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-400 text-center">깨봉 수학 교육 플랫폼 v3.0</p>
          </div>
        </div>
      </div>
    </>
  )
}


